import { useMemo, useState } from 'react'
import { ICard } from '../../../types/card'
import { EMediaType, TokenOriginalType } from '../../../types/lot'
import { originalToken as originalTokenAPI } from '../../../utils/api/index'
import { sendFiles } from '../../../utils/lot'
import { errors, notifications } from '../../../utils/notification'
import { File } from '../File'
import { InputWithState } from '../../UI/Input'
import { Checkbox } from '../../UI/Checkbox'
import { Counter } from '../../UI/Counter'
import { useDispatch } from 'react-redux'
import { action_setLoading } from '../../../store/actions/popup'
import { useValidateHelper } from '../../../hooks/useValidate'
import { useContext } from 'react'
import { EventContext } from '../../../ctx/event'
import { Button } from '../Buttons'
import { ISelectOption, Select } from '../Select'
import { useRadio } from '../../../hooks/useRadio'
import { PopupHalf, PopupHalfItem, PopupSubmit } from '../../../hoc/Popup'

interface IPropsLotEditor {
    card?: ICard
}

export function Editor({ card }: IPropsLotEditor) {
    const [name, setName] = useState(card?.name || '')
    const [description, setDescription] = useState(card?.description || '')
    const [copiesTotal, setCopiesTotal] = useState(card?.copiesTotal || 1)
    const [censored, setCensored] = useState(false)
    const [commercial, setCommercial] = useState(false)
    const [showErrors, setShowErrors] = useState(false)
    const [step, setStep] = useState(1)
    const [file, setFile] = useState<File>()
    const dispatch = useDispatch()
    const { onEditorCreated, onEditorErrorSendFile } = useContext(EventContext)

    type tokenTypes = 'nft' | 'premium'

    const [tokenType, setTokenType] = useState<tokenTypes>('nft')
    const radio = useRadio<tokenTypes>({
        value: [tokenType, setTokenType],
        indexes: ['nft', 'premium'],
    })

    const percentOptions: ISelectOption[] = useMemo(() => {
        return [
            { label: '0%', value: 0 },
            { label: '5%', value: 5 },
            { label: '10%', value: 10 },
            { label: '15%', value: 15 },
        ]
    }, [])
    const [percent, setPercent] = useState(
        percentOptions.find((o) => o.value === 10)
    )

    const nameError = useValidateHelper(
        'Artwork name is required',
        !!name.length
    )
    const descriptionError = useValidateHelper(
        'Artwork description is required',
        !!description.length
    )

    function nextStep() {
        setStep(step + 1)
    }

    async function sendData() {
        if (!file) {
            errors.pickTheFile()
            return
        }
        if (nameError || descriptionError) {
            errors.fillRequireFields()
            setShowErrors(true)
            return
        }
        dispatch(action_setLoading(true))
        const contentType = file?.type.match(/video/)
            ? EMediaType.VIDEO
            : EMediaType.IMAGE
        const type =
            tokenType === 'nft'
                ? TokenOriginalType.NFT_TOKEN
                : TokenOriginalType.PREMIUM_CONTENT

        const toServer = {
            name,
            description,
            copiesTotal,
            isUseCensored: censored,
            contentType,
            isCommercial: commercial,
            creatorReward: (percent?.value || 10) as 0 | 5 | 10 | 15,
            type,
        }

        try {
            const { data } = card?.id
                ? await originalTokenAPI.update(card.id, toServer)
                : await originalTokenAPI.create(toServer)

            await sendFiles(data.id, contentType, file).catch((e) => {
                if (onEditorErrorSendFile) onEditorErrorSendFile(data)

                errors.sendFile()
                return Promise.reject(e)
            })
            await originalTokenAPI.complete(data.id)
            notifications.NFTSendToModeration()
            if (onEditorCreated) onEditorCreated(data)
        } catch (e) {
            console.error(e)
        }

        dispatch(action_setLoading(false))
    }

    return (
        <div className="popup__tokeneditor">
            {card?.moderatorMessage ? (
                <div className="popup__exception">{card?.moderatorMessage}</div>
            ) : null}
            <div className="popup-progress">
                {[1, 2].map((s) => (
                    <div
                        key={s}
                        className={`popup-progress__item ${
                            step >= s ? 'popup-progress__item-active' : ''
                        }`}
                        onClick={() => setStep(s)}
                    ></div>
                ))}
            </div>
            <div className="popup__step">Step {step} from 2</div>
            {step === 1 ? (
                <>
                    <File
                        state={[file, setFile]}
                        accept={['.jpg', '.png', '.jpeg', '.mp4']}
                        minHeight={200}
                        minWidth={200}
                        maxSize={100}
                        title="Upload the artwork you will be selling"
                    ></File>
                    <PopupSubmit>
                        <Button onClick={nextStep}>Next step</Button>
                    </PopupSubmit>
                </>
            ) : null}
            {step === 2 ? (
                <>
                    <PopupHalf>
                        <PopupHalfItem>
                            <Checkbox
                                state={[
                                    radio.mainState.nft,
                                    () => radio.setState('nft'),
                                ]}
                                title={'NFT'}
                            ></Checkbox>
                        </PopupHalfItem>
                        <PopupHalfItem>
                            <Checkbox
                                state={[
                                    radio.mainState.premium,
                                    () => radio.setState('premium'),
                                ]}
                                title={'Premium content'}
                            ></Checkbox>
                        </PopupHalfItem>
                    </PopupHalf>
                    <InputWithState
                        state={[name, setName]}
                        title="Artwork name"
                        errorMessage={showErrors ? nameError : ''}
                    ></InputWithState>
                    <InputWithState
                        state={[description, setDescription]}
                        title="Description"
                        textarea
                        errorMessage={showErrors ? descriptionError : ''}
                    ></InputWithState>
                    {radio.mainState.nft && (
                        <>
                            <Select
                                options={percentOptions}
                                state={[percent, setPercent]}
                                title="Constant royalties amount"
                            ></Select>
                            <Checkbox
                                state={[censored, setCensored]}
                                title="Censored"
                            ></Checkbox>
                            <Checkbox
                                state={[commercial, setCommercial]}
                                title="This NFT comes with commercial rights"
                            ></Checkbox>

                            <Counter
                                state={[copiesTotal, setCopiesTotal]}
                                title="Copies"
                                min={1}
                                max={10}
                            ></Counter>
                        </>
                    )}
                    <PopupSubmit>
                        <Button onClick={sendData}>Create</Button>
                    </PopupSubmit>
                </>
            ) : null}
        </div>
    )
}
